
import {
  CartLinesUpdateEvent,
  StandardEvents,
} from 'https://cdn.shopify.com/storefront/standard-events.js';

if (!customElements.get('cart-pair-with')) {
  class CartPairWith extends HTMLElement {
    constructor() {
      super();
      this.requestId = 0;
      this.refreshTimer = null;
      this.adding = false;
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    connectedCallback() {
      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        this.onCartUpdate
      );
      window.addEventListener('pageshow', this.onPageShow);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener(
        StandardEvents.cartLinesUpdate,
        this.onCartUpdate
      );
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.refreshTimer);
      this.requestId += 1;
    }

    onPageShow(event) {
      if (event.persisted) this.scheduleRefresh();
    }

    onCartUpdate(event) {
      if (
        event.promise &&
        typeof event.promise.then === 'function'
      ) {
        event.promise.then(
          () => this.scheduleRefresh(),
          () => this.scheduleRefresh()
        );
      } else {
        this.scheduleRefresh();
      }
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);

      this.refreshTimer = setTimeout(() => {
        if (this.isConnected && !this.adding) {
          this.refresh();
        }
      }, 350);
    }

    get root() {
      return window.Shopify?.routes?.root || '/';
    }

    async getJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
      });

      let result;

      try {
        result = await response.json();
      } catch {
        throw new Error(
          'Unexpected response from Shopify.'
        );
      }

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Request failed.'
        );
      }

      return result;
    }

    async refresh() {
      if (this.adding) return;

      const currentRequest = ++this.requestId;

      try {
        const cart = await this.getJSON(
          `${this.root}cart.js`
        );

        if (
          !this.isConnected ||
          currentRequest !== this.requestId
        ) {
          return;
        }

        if (!cart.items?.length) {
          this.hidden = true;
          return;
        }

        const maximum = Math.min(
          10,
          Math.max(
            1,
            Number(this.dataset.maxProducts) || 3
          )
        );

        const alreadyInCart = new Set(
          cart.items.map(
            item => String(item.product_id)
          )
        );

        const alreadyChosen = new Set();
        const recommendations = [];

        for (const item of [...cart.items].reverse()) {
          if (recommendations.length >= maximum) {
            break;
          }

          const params = new URLSearchParams({
            product_id: String(item.product_id),
            intent: 'complementary',
            limit: '10',
          });

          let data;

          try {
            data = await this.getJSON(
              `${this.root}recommendations/products.json?${params}`
            );
          } catch (error) {
            console.warn(
              '[Pair it with] Recommendations unavailable:',
              error
            );
            continue;
          }

          if (
            !this.isConnected ||
            currentRequest !== this.requestId
          ) {
            return;
          }

          for (const product of data.products || []) {
            const id = String(product.id);

            if (
              alreadyInCart.has(id) ||
              alreadyChosen.has(id)
            ) {
              continue;
            }

            if (
              !(product.variants || []).some(
                variant => variant.available
              )
            ) {
              continue;
            }

            alreadyChosen.add(id);
            recommendations.push(product);

            if (recommendations.length >= maximum) {
              break;
            }
          }
        }

        if (
          !this.isConnected ||
          currentRequest !== this.requestId
        ) {
          return;
        }

        if (!recommendations.length) {
          this.hidden = true;
          return;
        }

        this.renderProducts(
          recommendations,
          cart.currency
        );
      } catch (error) {
        console.warn(
          '[Pair it with] Failed to load:',
          error
        );

        if (currentRequest === this.requestId) {
          this.hidden = true;
        }
      }
    }

    formatPrice(cents, currency) {
      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en',
          {
            style: 'currency',
            currency:
              currency ||
              window.Shopify?.currency?.active ||
              'EUR',
          }
        ).format(Number(cents) / 100);
      } catch {
        return (Number(cents) / 100).toFixed(2);
      }
    }

    getVariantValues(variant) {
      if (
        Array.isArray(variant.options) &&
        variant.options.length
      ) {
        return variant.options.map(
          value => String(value ?? '')
        );
      }

      return [
        variant.option1,
        variant.option2,
        variant.option3,
      ]
        .filter(value => value != null)
        .map(String);
    }

    getOptionGroups(product, variants) {
      const productOptions = Array.isArray(
        product.options
      )
        ? product.options
        : [];

      const firstVariantOptions =
        this.getVariantValues(variants[0]);

      const count = Math.max(
        productOptions.length,
        firstVariantOptions.length
      );

      const groups = [];

      for (let index = 0; index < count; index++) {
        const source = productOptions[index];

        const name =
          typeof source === 'string'
            ? source
            : source?.name || `Option ${index + 1}`;

        const values = [
          ...new Set(
            variants
              .map(
                variant =>
                  this.getVariantValues(variant)[index]
              )
              .filter(
                value =>
                  value !== undefined &&
                  value !== ''
              )
          ),
        ];

        if (!values.length) return [];

        groups.push({ name, values });
      }

      return groups;
    }

    renderProducts(products, currency) {
      const body = this.querySelector(
        '.cart-pair-with__body'
      );

      if (!body) return;

      body.replaceChildren();

      for (const product of products) {
        const card = this.createProductCard(
          product,
          currency
        );

        if (card) body.append(card);
      }

      this.hidden = body.children.length === 0;
    }

    createProductCard(product, currency) {
      const variants = (
        product.variants || []
      ).filter(
        variant => variant && variant.id
      );

      const available = variants.filter(
        variant => variant.available
      );

      if (!available.length) return null;

      const card = document.createElement('div');
      card.className = 'cart-pair-with__product';

      const imageLink = document.createElement('a');
      imageLink.href =
        product.url ||
        `${this.root}products/${product.handle}`;

      const imageURL =
        typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.src;

      if (imageURL) {
        const image = document.createElement('img');

        image.src = imageURL;
        image.alt = product.title || '';
        image.width = 78;
        image.height = 104;
        image.loading = 'lazy';
        image.className = 'cart-pair-with__image';

        imageLink.append(image);
      }

      const details = document.createElement('div');
      details.className = 'cart-pair-with__details';

      const title = document.createElement('a');
      title.href = imageLink.href;
      title.className = 'cart-pair-with__title';
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'cart-pair-with__price';

      const error = document.createElement('span');
      error.className = 'cart-pair-with__error';
      error.setAttribute('role', 'alert');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-pair-with__button';

      details.append(title, price);

      let selectedVariant =
        variants.length === 1
          ? variants[0]
          : null;

      let busy = false;
      let addedSuccessfully = false;

      const optionSelectors = [];
      let fallbackSelector = null;

      const update = () => {
        if (fallbackSelector) {
          selectedVariant =
            variants.find(
              variant =>
                String(variant.id) ===
                fallbackSelector.value
            ) || null;
        } else if (optionSelectors.length) {
          const values = optionSelectors.map(
            select => select.value
          );

          selectedVariant = values.every(Boolean)
            ? variants.find(variant => {
                const variantValues =
                  this.getVariantValues(variant);

                return values.every(
                  (value, index) =>
                    variantValues[index] === value
                );
              }) || null
            : null;
        }

        if (
          selectedVariant &&
          selectedVariant.available
        ) {
          price.textContent = this.formatPrice(
            selectedVariant.price,
            currency
          );

          button.textContent =
            this.dataset.labelAdd ||
            'ADD TO CART';
        } else {
          const minPrice = Math.min(
            ...available.map(
              variant => Number(variant.price)
            )
          );

          price.textContent = `${
            this.dataset.labelFrom || 'From'
          } ${this.formatPrice(minPrice, currency)}`;

          if (
            selectedVariant &&
            !selectedVariant.available
          ) {
            button.textContent =
              this.dataset.labelSoldout ||
              'Sold out';
          } else if (
            optionSelectors.length &&
            optionSelectors.every(
              select => select.value
            )
          ) {
            button.textContent =
              this.dataset.labelUnavailable ||
              'Unavailable combination';
          } else {
            button.textContent =
              this.dataset.labelChooseOptions ||
              'CHOOSE OPTIONS';
          }
        }

        button.disabled =
          this.adding ||
          busy ||
          addedSuccessfully ||
          !selectedVariant?.available;

        if (addedSuccessfully) {
          button.textContent =
            this.dataset.labelAdded ||
            'ADDED';
        }
      };

      if (variants.length > 1) {
        const groups = this.getOptionGroups(
          product,
          variants
        );

        if (groups.length) {
          for (const group of groups) {
            const label =
              document.createElement('label');

            label.className =
              'cart-pair-with__option';

            const labelText =
              document.createElement('span');

            labelText.className =
              'cart-pair-with__option-label';

            labelText.textContent = group.name;

            const select =
              document.createElement('select');

            select.className =
              'cart-pair-with__select';

            select.setAttribute(
              'aria-label',
              group.name
            );

            select.add(
              new Option(
                `${
                  this.dataset.labelChoose ||
                  'Choose'
                } ${group.name}`,
                ''
              )
            );

            for (const value of group.values) {
              select.add(
                new Option(value, value)
              );
            }

            select.addEventListener(
              'change',
              () => {
                error.textContent = '';
                update();
              }
            );

            optionSelectors.push(select);

            label.append(labelText, select);
            details.append(label);
          }
        } else {
          fallbackSelector =
            document.createElement('select');

          fallbackSelector.className =
            'cart-pair-with__select';

          fallbackSelector.setAttribute(
            'aria-label',
            this.dataset.labelChoose ||
            'Choose variant'
          );

          fallbackSelector.add(
            new Option(
              this.dataset.labelChoose ||
              'Choose variant',
              ''
            )
          );

          for (const variant of variants) {
            const option = new Option(
              variant.public_title ||
              variant.title ||
              String(variant.id),
              String(variant.id)
            );

            option.disabled = !variant.available;
            fallbackSelector.add(option);
          }

          fallbackSelector.addEventListener(
            'change',
            () => {
              error.textContent = '';
              update();
            }
          );

          details.append(fallbackSelector);
        }
      }

      button.addEventListener(
        'click',
        async () => {
          update();

          if (
            this.adding ||
            busy ||
            addedSuccessfully ||
            !selectedVariant?.available
          ) {
            return;
          }

          this.adding = true;
          busy = true;

          button.disabled = true;
          button.textContent =
            this.dataset.labelAdding ||
            'ADDING...';

          error.textContent = '';

          try {
            await this.addToCart(
              selectedVariant.id
            );

            addedSuccessfully = true;
            this.scheduleRefresh();
          } catch (cause) {
            console.error(
              '[Pair it with] Add to cart failed:',
              cause
            );

            error.textContent =
              cause.message ||
              this.dataset.labelError ||
              'Could not add to cart.';
          } finally {
            this.adding = false;
            busy = false;

            update();
            this.scheduleRefresh();
          }
        }
      );

      details.append(button, error);
      card.append(imageLink, details);

      update();

      return card;
    }

    async addToCart(variantId) {
      const ids = [
        ...new Set(
          [
            ...document.querySelectorAll(
              'cart-items-component[data-section-id]'
            ),
          ]
            .map(
              element => element.dataset.sectionId
            )
            .filter(Boolean)
        ),
      ].slice(0, 5);

      if (!ids.length) {
        throw new Error(
          'Cart section not found.'
        );
      }

      const deferred =
        CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [
            {
              merchandiseId: String(variantId),
              quantity: 1,
            },
          ],
          promise: deferred.promise,
        })
      );

      try {
        // Only ONE Shopify add-to-cart request.
        const added = await this.getJSON(
          `${this.root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1,
                },
              ],
              sections: ids.join(','),
              sections_url:
                window.location.pathname,
            }),
          }
        );

        const cart = await this.getJSON(
          `${this.root}cart.js`
        );

        // Newer Horizon cart update event.
        deferred.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              cart
            ),
          detail: {
            sections: added.sections || {},
            items: cart.items,
            itemCount: cart.item_count,
            source: 'cart-pair-with',
            didError: false,
          },
        });

        // Legacy Horizon cart update event.
        // Required by older Horizon versions.
        const addedItem = cart.items?.find(
          item =>
            String(item.variant_id) ===
            String(variantId)
        );

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'cart-pair-with',
              data: {
                source:
                  'product-form-component',
                itemCount: cart.item_count,
                productId:
                  addedItem?.product_id,
                items: cart.items,
                sections: added.sections || {},
              },
            },
          })
        );
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    }
  }

  customElements.define(
    'cart-pair-with',
    CartPairWith
  );
}
